/**
 * Django API client for Pay4Me
 * Replaces Firebase Firestore calls with REST API calls
 */

import type {
  PublicCampaignResponse,
  ContributionCreateRequest,
  ContributionCreateResponse,
  ContributionStatusResponse,
  ApiError,
} from '../types/api';
import { API_BASE_URL } from './constant';

/**
 * Custom error class for API errors
 */
export class Pay4MeApiError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code: string = 'unknown_error', details?: unknown) {
    super(message);
    this.name = 'Pay4MeApiError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Handle API response and extract data or throw error
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError | null = null;
    try {
      errorData = await response.json();
    } catch {
      // Response may not be JSON
    }

    if (response.status === 404) {
      throw new Pay4MeApiError(
        errorData?.message || 'Campaign not found',
        errorData?.code || 'not_found'
      );
    }

    if (response.status === 429) {
      throw new Pay4MeApiError(
        errorData?.message || 'Too many requests. Please try again later.',
        errorData?.code || 'rate_limited'
      );
    }

    throw new Pay4MeApiError(
      errorData?.message || `Request failed with status ${response.status}`,
      errorData?.code || 'api_error',
      errorData?.details
    );
  }

  return response.json();
}

/**
 * Fetch public campaign details by sharing token.
 * Contributor app resolves the token from the URL path.
 */
export async function getCampaign(token: string): Promise<PublicCampaignResponse> {
  const response = await fetch(`${API_BASE_URL}/pay4me/${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  return handleResponse<PublicCampaignResponse>(response);
}

/**
 * Fetch public campaign details by sharing token
 * Alternative to getCampaign when we have the token instead of ID
 */
export async function getCampaignByToken(token: string): Promise<PublicCampaignResponse> {
  return getCampaign(token);
}

/**
 * Create a contribution to a campaign
 * This initiates the payment flow through Django backend
 */
export async function createContribution(
  token: string,
  data: ContributionCreateRequest
): Promise<ContributionCreateResponse> {
  const response = await fetch(`${API_BASE_URL}/pay4me/${encodeURIComponent(token)}/contribute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      amount: data.amount,
      contributor_name: data.contributor_name,
      contributor_contact: data.contributor_contact,
      is_anonymous: data.is_anonymous ?? false,
      message: data.message,
      phone_number: data.phone_number,
      // For Paystack we use "debit_card" (backend resolves provider=PAYSTACK_CARD)
      payment_method: data.payment_method || (data.payment_provider ? undefined : 'debit_card'),
      payment_provider: data.payment_provider,
      idempotency_key: data.idempotency_key,
    }),
  });

  return handleResponse<ContributionCreateResponse>(response);
}

/**
 * Get contribution status
 * Used to poll for payment completion after Paystack redirect
 */
export async function getContributionStatus(
  contributionId: string
): Promise<ContributionStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/pay4me/contributions/${contributionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  return handleResponse<ContributionStatusResponse>(response);
}

/**
 * Poll for contribution status until completed or failed
 * Returns when status changes from pending/processing
 */
export async function pollContributionStatus(
  contributionId: string,
  options: {
    maxAttempts?: number;
    intervalMs?: number;
    onPoll?: (attempt: number, status: ContributionStatusResponse) => void;
  } = {}
): Promise<ContributionStatusResponse> {
  const { maxAttempts = 30, intervalMs = 2000, onPoll } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const status = await getContributionStatus(contributionId);

    if (onPoll) {
      onPoll(attempt, status);
    }

    // Return if we've reached a terminal status
    const normalized = String(status.status || '').toUpperCase();
    if (['COMPLETED', 'FAILED', 'CANCELLED', 'REFUNDED'].includes(normalized)) {
      return status;
    }

    // Wait before next poll
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  // Return last status if max attempts reached
  return getContributionStatus(contributionId);
}
